/**
 * Paar-Review fuer Uebersetzungen (Design 15c, KONZEPT-MEHRSPRACHIGKEIT Neufassung):
 * DER eine Trichter, in dem BEIDE Erfassungswege enden - PDF-Zuordnung (verankertes
 * Matching) und KI-Uebersetzung. Tabellen-Layout: links das Original (Name + Preis als
 * Anker), rechts die editierbare Uebersetzung. Haekchen je Paar, Alle-ab-/auswaehlen,
 * lange Listen eingeklappt. Es wird NIE blind uebernommen - der Mensch bestaetigt.
 */
import { useState } from "react";
import { Icon, Input, Checkbox } from "./components";

export interface TranslationPair {
  targetId: string;
  targetName: string;
  targetPriceCents?: number;
  name: string;
  description: string | null;
  confidence?: number;
  /** Zeile uebernehmen? (Default an; der Betreiber kann einzelne Paare abwaehlen.) */
  include: boolean;
}

/**
 * Exportiert (Nike-Flury-Datenluecken, 06.09.2026): `anfrage-detail.tsx` uebersetzt damit den
 * rohen `inquiries.language`-Code fuer die Anzeige, statt eine zweite Zuordnung zu erfinden.
 */
export const LANG_LABEL: Record<string, string> = { en: "Englisch", it: "Italienisch", fr: "Französisch", es: "Spanisch", de: "Deutsch" };

function euro(cents: number | undefined): string | null {
  if (cents == null || cents <= 0) return null;
  return (cents / 100).toFixed(2).replace(".", ",") + " €";
}

/** Bei langen Karten zuerst nur ein Ausschnitt - der Rest hinter "N weitere Paare anzeigen". */
const COLLAPSED_ROWS = 8;

export function TranslationPairReview({
  pairs,
  lang,
  notFound,
  onChange,
}: {
  pairs: TranslationPair[];
  lang: string;
  notFound: number;
  onChange: (next: TranslationPair[]) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const patch = (idx: number, p: Partial<TranslationPair>) => onChange(pairs.map((row, i) => (i === idx ? { ...row, ...p } : row)));
  const included = pairs.filter((p) => p.include).length;
  const allSelected = included === pairs.length;
  const lowConfidence = pairs.filter((p) => (p.confidence ?? 1) < 0.75).length;
  const visible = expanded ? pairs : pairs.slice(0, COLLAPSED_ROWS);
  const hidden = pairs.length - visible.length;
  return (
    <div className="overflow-hidden rounded-xl border border-outline-variant/40 bg-surface-container-lowest dark:border-outline/30 dark:bg-inverse-surface">
      {/* Kopf: Zaehler + Sammel-Auswahl */}
      <div className="flex flex-wrap items-center gap-3 border-b border-outline-variant/30 px-4 py-3 dark:border-outline/30">
        <p className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
          <span className="font-bold">{pairs.length} Zuordnungen</span>{" "}
          <span className="text-on-surface-variant dark:text-secondary-fixed-dim">{LANG_LABEL[lang] ?? lang.toUpperCase()}</span>
        </p>
        {lowConfidence > 0 && (
          <span className="rounded bg-secondary/15 px-1.5 py-0.5 font-label-md text-[11px] font-semibold text-secondary dark:text-[#b8ae9e]">
            {lowConfidence} unsicher — bitte prüfen
          </span>
        )}
        {notFound > 0 && (
          <span className="rounded bg-surface-container-high px-1.5 py-0.5 font-label-md text-[11px] font-semibold text-on-surface-variant dark:bg-white/[0.08]">
            {notFound} ohne Treffer (bleiben unübersetzt)
          </span>
        )}
        <span className="ml-auto flex items-center gap-3">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-label-md text-[11px] font-semibold ${
              allSelected
                ? "bg-[#F0EAE0] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]"
                : "bg-surface-container-high text-on-surface-variant dark:bg-white/[0.08]"
            }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${allSelected ? "bg-secondary dark:bg-secondary-fixed-dim" : "bg-on-surface-variant/50"}`} />
            {allSelected ? "Alle ausgewählt" : `${included} von ${pairs.length} ausgewählt`}
          </span>
          <button
            type="button"
            className="font-label-md text-[12px] font-semibold text-on-surface-variant underline hover:text-on-background dark:hover:text-inverse-on-surface"
            onClick={() => onChange(pairs.map((p) => ({ ...p, include: !allSelected })))}
          >
            {allSelected ? "Alle abwählen" : "Alle auswählen"}
          </button>
        </span>
      </div>

      {/* Spaltenkoepfe */}
      <div className="grid grid-cols-[24px_1fr_20px_1fr] items-center gap-3 border-b border-outline-variant/30 px-4 py-2 dark:border-outline/30">
        <span />
        <span className="font-label-md text-[11px] font-semibold uppercase tracking-wide text-on-surface-variant/70">Original</span>
        <span />
        <span className="font-label-md text-[11px] font-semibold uppercase tracking-wide text-on-surface-variant/70">
          Übersetzung ({LANG_LABEL[lang] ?? lang.toUpperCase()}) · editierbar
        </span>
      </div>

      {/* Paar-Zeilen */}
      <div className="divide-y divide-outline-variant/20 dark:divide-outline/20">
        {visible.map((p, idx) => {
          const unsure = (p.confidence ?? 1) < 0.75;
          return (
            <div
              key={p.targetId}
              className={`grid grid-cols-[24px_1fr_20px_1fr] items-center gap-3 px-4 py-2.5 ${
                unsure ? "bg-secondary/5" : ""
              } ${p.include ? "" : "opacity-45"}`}
            >
              <Checkbox
                checked={p.include}
                onChange={(e) => patch(idx, { include: e.target.checked })}
                title="Diese Zuordnung übernehmen"
              />
              <div className="min-w-0">
                <p className="truncate font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface" title={p.targetName}>
                  {p.targetName}
                </p>
                <p className="font-body-sm text-[12px] text-on-surface-variant/70">
                  {euro(p.targetPriceCents) ?? ""}
                  {unsure && (
                    <span className="ml-1.5 font-semibold text-secondary dark:text-[#b8ae9e]">Zuordnung unsicher</span>
                  )}
                </p>
              </div>
              <Icon name="arrow_forward" className="text-[15px] text-on-surface-variant/50" />
              <div className="min-w-0 space-y-1">
                <Input className="!py-1.5 text-body-sm" value={p.name} disabled={!p.include} onChange={(e) => patch(idx, { name: e.target.value })} />
                {/* Beschreibung nur zeigen, wenn die Uebersetzung eine mitbringt - keine leeren
                    Textareas (Design-15c-Entscheidung). Leeren loescht die Uebersetzungs-Beschreibung. */}
                {p.description != null && p.description !== "" && (
                  <Input
                    className="!py-1.5 text-[12px]"
                    value={p.description}
                    disabled={!p.include}
                    onChange={(e) => patch(idx, { description: e.target.value || null })}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Collapse-Fusszeile */}
      {hidden > 0 && (
        <button
          type="button"
          className="flex w-full items-center justify-center gap-1.5 border-t border-outline-variant/30 py-2.5 font-body-sm text-body-sm text-on-surface-variant hover:bg-surface-container-high/40 dark:border-outline/30 dark:hover:bg-surface-variant/20"
          onClick={() => setExpanded(true)}
        >
          <Icon name="expand_more" className="text-[18px]" /> {hidden} weitere Paare anzeigen
        </button>
      )}
      {expanded && pairs.length > COLLAPSED_ROWS && (
        <button
          type="button"
          className="flex w-full items-center justify-center gap-1.5 border-t border-outline-variant/30 py-2.5 font-body-sm text-body-sm text-on-surface-variant hover:bg-surface-container-high/40 dark:border-outline/30 dark:hover:bg-surface-variant/20"
          onClick={() => setExpanded(false)}
        >
          <Icon name="expand_less" className="text-[18px]" /> Weniger anzeigen
        </button>
      )}
    </div>
  );
}
